import React, { useContext, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import AuthClient from '../../api/AuthClient';
import { useNavigate } from 'react-router-dom';
import DeleteGroupApi from '../../api/DeleteGroupApi';
import UnacceptOfferApi from '../../api/UnAcceptAnOfferApi';
import { PartiesContext } from '../../GlobalContext/partiesContext';
import ReactiveButton from 'reactive-button';
import { deleteGroupAndCloseModal } from './deleteModalActions';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../query/queryKeys';

interface DeleteModalProps {
  show: boolean;
  message: string;
  onHide: () => void;
  offerId: string | undefined;
  groupId: string;
  anyAcceptedOffer: boolean
}

type RequestState = 'idle' | 'loading' | 'success' | 'error';

const DeleteModal: React.FC<DeleteModalProps> = ({ show, message, onHide, offerId, groupId, anyAcceptedOffer }) => {

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isConfirmed, setConfirmation] = useState<boolean>(false);
  const [deleteRequestState, setDeleteRequestState] = useState<RequestState>('idle');
  const [unacceptOfferRequestState, setUnacceptOfferRequestState] = useState<RequestState>('idle');
  const { myPartiesInfo, setMyPartiesInfoContextData } = useContext(PartiesContext);
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const isDeleteLoading = deleteRequestState === 'loading';
  const isUnacceptOfferLoading = unacceptOfferRequestState === 'loading';

  useEffect(() => {
    if (!show && !isModalOpen && !isConfirmed) {
      setDeleteRequestState('idle');
      setUnacceptOfferRequestState('idle');
    }
  }, [show, isModalOpen, isConfirmed]);

  const skipOffer = () => {
    if (isUnacceptOfferLoading) {
      return;
    }

    setConfirmation(true);
    setModalOpen(false);
  };

  const onNext = () => {
    if (isDeleteLoading) {
      return;
    }

    if (anyAcceptedOffer) {
      onHide();
      setModalOpen(true);
    } else {
      void confirmDeleteGroup();
    }
  }

  const notConfirmed = () => {
    if (isUnacceptOfferLoading) {
      return;
    }

    setConfirmation(false);
  }

  const confirmSkipOffer = async () => {
    if (!offerId || isUnacceptOfferLoading) {
      return;
    }

    const authClient = new AuthClient();
    const api = new UnacceptOfferApi(authClient);

    try {
      setUnacceptOfferRequestState('loading');
      const response = await api.unAcceptOffer(offerId);

      if (!response || response.statusCode >= 400) {
        throw new Error('Failed to unaccept the offer.');
      }

      setUnacceptOfferRequestState('success');
      void queryClient.invalidateQueries({ queryKey: queryKeys.myParties() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myParty(groupId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.promoterGroupsRoot() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.acceptedGroups() });
      setConfirmation(false);
      setModalOpen(false);
    } catch (error) {
      console.error('Failed to unaccept offer', error);
      setUnacceptOfferRequestState('error');
    }
  }

  const confirmDeleteGroup = async () => {
    if (isDeleteLoading) {
      return;
    }

    const authClient = new AuthClient();
    const deleteGroupApi = new DeleteGroupApi(authClient);

    try {
      setDeleteRequestState('loading');
      await deleteGroupAndCloseModal({
        groupId,
        myPartiesInfo,
        deleteGroup: (currentGroupId) => deleteGroupApi.deleteGroup(currentGroupId),
        setMyPartiesInfoContextData,
        onHide,
        navigate,
      });
      queryClient.removeQueries({ queryKey: queryKeys.myParty(groupId) });
      queryClient.removeQueries({ queryKey: queryKeys.promoterGroup(groupId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.myParties() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.promoterGroupsRoot() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.acceptedGroups() });
      setDeleteRequestState('success');
      setModalOpen(false);
      setConfirmation(false);
    } catch (error) {
      console.error('Failed to delete group', error);
      setDeleteRequestState('error');
    }
  }

  return (
    <>
      <Modal
        show={show}
        backdrop={false}
        centered
        onHide={() => {
          if (!isDeleteLoading) {
            onHide();
          }
        }}
        keyboard={!isDeleteLoading}
      >
        <Modal.Body className="text-center">
          {message}
        </Modal.Body>

        <Modal.Footer>
          <button
            className="btn btn-sm btn-outline-warning"
            onClick={onHide}
            disabled={isDeleteLoading}
          >
            No
          </button>
          <ReactiveButton
            buttonState={deleteRequestState}
            onClick={() => { void onNext(); }}
            color={'red'}
            idleText={'Yes'}
            loadingText={'Deleting'}
            successText={'Deleted'}
            errorText={'Error'}
            type={'button'}
            className="reactive-btn btn btn-sm btn-outline-danger red outline"
            outline={false}
            shadow={false}
            rounded={false}
            size={'normal'}
            block={false}
            messageDuration={2000}
            disabled={isDeleteLoading}
            buttonRef={null}
            width={null}
            height={null}
            animation={true}
          />
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={isModalOpen}
        onHide={() => {
          if (!isDeleteLoading) {
            setModalOpen(false);
          }
        }}
        keyboard={!isDeleteLoading}
      >
        <Modal.Header closeButton={!isDeleteLoading} />

        <Modal.Body className="d-flex flex-column gap-3 px-4 py-4">
          <button
            type="button"
            className="link btns btnMO w-100 text-center"
            onClick={skipOffer}
            disabled={isDeleteLoading || isUnacceptOfferLoading}
          >
            Skip accepted offer so I can pick a new offer
          </button>
          <ReactiveButton
            buttonState={deleteRequestState}
            onClick={() => { void confirmDeleteGroup(); }}
            color={'red'}
            idleText={'I really do want to delete the group'}
            loadingText={'Deleting'}
            successText={'Deleted'}
            errorText={'Error'}
            type={'button'}
            className="link btns btnMO"
            outline={false}
            shadow={false}
            rounded={false}
            size={'normal'}
            block={true}
            messageDuration={2000}
            disabled={isDeleteLoading || isUnacceptOfferLoading}
            buttonRef={null}
            width={null}
            height={null}
            animation={true}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={isConfirmed}
        centered
        onHide={() => {
          if (!isUnacceptOfferLoading) {
            setConfirmation(false);
          }
        }}
        keyboard={!isUnacceptOfferLoading}
      >
        <Modal.Body className="text-center">
          Are you  sure you want to change your mind and no longer consider this offer as the accepted?
        </Modal.Body>

        <Modal.Footer>
          <button
            className="link btns btnMO justify-content-center d-flex mt-3 align-content-center p-3 m-3"
            onClick={notConfirmed}
            disabled={isUnacceptOfferLoading}
          >
            No
          </button>

          <ReactiveButton
            buttonState={unacceptOfferRequestState}
            onClick={() => { void confirmSkipOffer(); }}
            color={'red'}
            idleText={'Yes'}
            loadingText={'Updating'}
            successText={'Updated'}
            errorText={'Error'}
            type={'button'}
            className="link btns btnMO justify-content-center d-flex mt-3 align-content-center p-3 m-3"
            outline={false}
            shadow={false}
            rounded={false}
            size={'normal'}
            block={false}
            messageDuration={2000}
            disabled={isUnacceptOfferLoading}
            buttonRef={null}
            width={null}
            height={null}
            animation={true}
          />
        </Modal.Footer>
      </Modal>
    </>

  );
};

export default DeleteModal;
